/* eslint-disable jsx-a11y/label-has-associated-control */
import { FormControl, InputAdornment, MenuItem, Select, TextField } from '@material-ui/core';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { FC, MouseEvent, useRef } from 'react';
import { DIPLOMA_OPTIONS, GENDER_OPTIONS, LANGUAGE_OPTIONS } from '../../shared/constants';
import { useStyles } from '../styles';
import { InfosForm } from '../types';

interface StepInfosProps {
  infos: InfosForm;
  onChange: (patch: Partial<InfosForm>) => void;
}

const StepInfos: FC<StepInfosProps> = ({ infos, onChange }) => {
  const classes = useStyles();
  const fieldClass = clsx(classes.textField, classes.fullField);
  const birthInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const input = birthInputRef.current;
    if (!input) {
      return;
    }
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Already open or not allowed in this browser
      }
    }
    input.focus();
  };

  return (
    <div>
      <h2 className={classes.sectionTitle}>Informations</h2>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="infos-firstname">
          Prénom
        </label>
        <TextField
          id="infos-firstname"
          className={fieldClass}
          variant="outlined"
          value={infos.firstname}
          onChange={(event) => onChange({ firstname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="infos-lastname">
          Nom
        </label>
        <TextField
          id="infos-lastname"
          className={fieldClass}
          variant="outlined"
          value={infos.lastname}
          onChange={(event) => onChange({ lastname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="infos-birth">
            Date de naissance
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <TextField
          id="infos-birth"
          className={fieldClass}
          variant="outlined"
          type="date"
          value={infos.dayOfBirth}
          inputRef={birthInputRef}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => onChange({ dayOfBirth: event.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <button
                  type="button"
                  className={classes.datePickerBtn}
                  aria-label="Ouvrir le calendrier"
                  onClick={openDatePicker}>
                  <CalendarBlankIcon size={20} color="#8B90A5" />
                </button>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="infos-gender">
            Sexe
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <FormControl variant="outlined" className={fieldClass}>
          <Select
            id="infos-gender"
            value={infos.gender}
            displayEmpty={true}
            onChange={(event) => onChange({ gender: event.target.value as string })}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {GENDER_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.postalVilleRow}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="infos-postal">
            Code Postal
          </label>
          <TextField
            id="infos-postal"
            className={fieldClass}
            variant="outlined"
            value={infos.postal_code}
            onChange={(event) => onChange({ postal_code: event.target.value })}
          />
        </div>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="infos-town">
            Ville
          </label>
          <TextField
            id="infos-town"
            className={fieldClass}
            variant="outlined"
            value={infos.town}
            onChange={(event) => onChange({ town: event.target.value })}
          />
        </div>
      </div>

      <div className={classes.field}>
        <label className={classes.label}>Téléphone</label>
        <div className={classes.phoneField}>
          <MaterialPhoneInput
            value={infos.phone as any}
            name="phone"
            onChange={(value) => onChange({ phone: value || '' })}
          />
        </div>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="infos-language">
          Langue
        </label>
        <FormControl variant="outlined" className={fieldClass}>
          <Select
            id="infos-language"
            name="language"
            value={infos.language === '' ? '' : String(infos.language)}
            displayEmpty={true}
            onChange={(event) => {
              const value = event.target.value as string;
              onChange({ language: value === '' ? '' : Number(value) });
            }}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.field}>
        <div className={classes.labelRow}>
          <label className={classes.label} htmlFor="infos-diploma">
            Diplôme
          </label>
          <span className={classes.optional}>Facultatif</span>
        </div>
        <FormControl variant="outlined" className={fieldClass}>
          <Select
            id="infos-diploma"
            value={infos.diploma}
            displayEmpty={true}
            onChange={(event) => onChange({ diploma: event.target.value as string })}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner</span>
            </MenuItem>
            {DIPLOMA_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

export default StepInfos;
